// @flow
import type { Item, CommunitySuggestion } from './types';

const WORD_CHAR_PATTERN = /[A-Za-z0-9ÄÖÜäöüß]/;
// Treat trailing geographic annexes as non-essential for matching.
// take out "am Neckar" and "im Breisgau" from the end of community names for matching purposes, but keep them in the displayed value.
const TRAILING_ANNEX_PATTERN =
  /\s+(am|im)\s+[A-Za-zÄÖÜäöüß.-]+(?:\s+[A-Za-zÄÖÜäöüß.-]+)*$/;

function normalizeCommunityForMatching(value: string): string {
  return value.replace(TRAILING_ANNEX_PATTERN, '');
}

function isWordBoundary(value: string, index: number): boolean {
  if (index <= 0) return true;
  // A match is valid only if it starts after a non-word character.
  return !WORD_CHAR_PATTERN.test(value[index - 1]);
}

function getPrefixWordMatchIndex(value: string, queryText: string): number {
  let idx = value.indexOf(queryText);
  while (idx !== -1) {
    if (isWordBoundary(value, idx)) {
      return idx;
    }
    idx = value.indexOf(queryText, idx + 1);
  }
  return -1;
}

function getUniqueAssociatedCommunities(items?: Array<Item>): Array<string> {
  if (!items || items.length === 0) return [];

  const communitiesByNormalizedValue: Map<string, string> = new Map();

  items.forEach((item) => {
    (item.associatedcommunitiesarray || []).forEach((community) => {
      const trimmed = (community || '').trim();
      if (!trimmed) return;

      const normalized = trimmed.toLowerCase();
      // Keep first spelling, dedupe case-insensitively.
      if (!communitiesByNormalizedValue.has(normalized)) {
        communitiesByNormalizedValue.set(normalized, trimmed);
      }
    });
  });

  return Array.from(communitiesByNormalizedValue.values());
}

export async function searchAssociatedCommunities(
  items?: Array<Item>,
  queryText?: string,
): Promise<Array<CommunitySuggestion>> {
  const query = (queryText || '').trim().toLowerCase();
  // Avoid noisy suggestions for very short inputs.
  if (query.length < 2) return [];

  const communities = getUniqueAssociatedCommunities(items);

  const ranked = communities
    .map((community) => {
      const normalizedCommunity = community.toLowerCase();
      // Match against normalized text without trailing annexes.
      const searchableCommunity =
        normalizeCommunityForMatching(normalizedCommunity);
      const matchIndex = getPrefixWordMatchIndex(searchableCommunity, query);
      if (matchIndex === -1) return null;

      return {
        value: community,
        matchIndex,
        matchLength: query.length,
        exact: normalizedCommunity === query,
        startsWith: matchIndex === 0,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      // Ranking: exact match > starts at beginning > earlier word position > shorter label > alpha.
      if (a.exact !== b.exact) return a.exact ? -1 : 1;
      if (a.startsWith !== b.startsWith) return a.startsWith ? -1 : 1;
      if (a.matchIndex !== b.matchIndex) return a.matchIndex - b.matchIndex;
      if (a.value.length !== b.value.length)
        return a.value.length - b.value.length;
      return a.value.localeCompare(b.value, 'de', { sensitivity: 'base' });
    });

  return ranked.map(({ value, matchIndex, matchLength }) => ({
    value,
    matchIndex,
    matchLength,
  }));
}

// Returns a comparator for Array.prototype.sort: exact-length matches first, then by score
function getFuseSortComparator(queryText: string) {
  const queryLength = queryText.length;
  return (a: any, b: any) => {
    const aExact = a.value.length === queryLength;
    const bExact = b.value.length === queryLength;
    if (aExact && !bExact) return -1;
    if ((!aExact && bExact) || (aExact && bExact)) return 1;
    // If both are not exact, sort by score
    return (a.score || 0) - (b.score || 0);
  };
}
function markFuseMatches(
  value: string,
  indices: Array<[number, number]>,
): string {
  if (!indices || indices.length === 0) return value;
  // Sort indices by start position
  const sorted = [...indices].sort((a, b) => a[0] - b[0]);
  let result = '';
  let lastIndex = 0;
  sorted.forEach(([start, end]) => {
    // Add text before match
    result += value.slice(lastIndex, start);
    // Add marked match
    result += `<mark class="text-highlight" data-markjs="true">${value.slice(
      start,
      end + 1,
    )}</mark>`;
    lastIndex = end + 1;
  });
  // Add remaining text after last match
  result += value.slice(lastIndex);
  return result;
}

export async function searchAuthorities(
  items?: Array<Item>,
  queryText?: string,
  options: { fuse: any, maxScore: number },
): Promise<Array<Item>> {
  const { fuse, maxScore } = options;
  if (!queryText || !queryText.trim()) {
    // Show all if no filter is set
    return items || [];
  }
  // combine filters
  const findings = fuse.search(queryText);
  const exactMatches = [];
  const grouped = findings.reduce((filtered, item) => {
    const i = item.item;
    const markedStrings = item.matches
      .sort(getFuseSortComparator(queryText))
      .map((m) => markFuseMatches(m.value, m.indices));
    i.highlighted = markedStrings.join(', ');

    if (item?.score <= maxScore) {
      filtered.push(i);
    }
    // Exact match should be 0 but is sometimes a very small number like 2.220446049250313e-16
    const wordLengths = item.matches.map((m) => m.value.length);
    if (
      item.score <= Number.EPSILON &&
      wordLengths.includes(queryText.length)
    ) {
      exactMatches.push(i);
    }
    return filtered;
  }, []);

  if (exactMatches.length > 0) {
    return exactMatches;
  }
  if (grouped.length > 0) {
    return grouped;
  }
  // fallback: best match if score is too high and no group filter is set
  return findings.length > 0 ? [findings[0].item] : [];
}

export default searchAuthorities;
